import { intlLocaleFor } from '@/components/user-portal/learner-i18n'
import { courseLiveHref } from '@/lib/lms/course-admin-href'
import type { VcroNextStep } from './types'

/**
 * Numbers on vCRO screens: the interface language's grouping, Latin digits
 * (intlLocaleFor), and a real minus sign. Each signed value is shown left to
 * right by the caller (`dir="ltr"`), so "+9.1" never reads "9.1+" in Arabic.
 */

const MINUS = '−'

const cache = new Map<string, Intl.NumberFormat>()
function nf(locale: string, digits: number): Intl.NumberFormat {
  const key = `${locale}|${digits}`
  let f = cache.get(key)
  if (!f) {
    f = new Intl.NumberFormat(intlLocaleFor(locale), { minimumFractionDigits: 0, maximumFractionDigits: digits })
    cache.set(key, f)
  }
  return f
}

/** A score: up to one decimal ("60", "60.4"). */
export function formatScore(value: number, locale: string): string {
  return nf(locale, 1).format(value)
}

/** A count ("1,234"). */
export function formatCount(value: number, locale: string): string {
  return nf(locale, 0).format(value)
}

/** Signed points, always one decimal: "+9.1", "−6.0", "0.0". */
export function formatPoints(points: number, locale: string): string {
  const f = new Intl.NumberFormat(intlLocaleFor(locale), { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const abs = f.format(Math.abs(points))
  if (Math.abs(points) < 0.05) return f.format(0)
  return `${points > 0 ? '+' : MINUS}${abs}`
}

/** A signed whole change in score ("+4", "−3"); 0 reads "0". */
export function formatChange(change: number, locale: string): string {
  const abs = nf(locale, 1).format(Math.abs(change))
  if (change === 0) return abs
  return `${change > 0 ? '+' : MINUS}${abs}`
}

/** A weight after fading, two decimals ("0.73"). */
export function formatWeight(weight: number, locale: string): string {
  return new Intl.NumberFormat(intlLocaleFor(locale), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(weight)
}

/** A share as a whole percentage of a part weight (0.6 -> "60%"). */
export function formatWeightShare(weight: number, locale: string): string {
  return new Intl.NumberFormat(intlLocaleFor(locale), { style: 'percent', maximumFractionDigits: 0 }).format(weight)
}

/**
 * Where a next step's link goes. The API names the kind of action and, when
 * it has one, the thing to open; the page it opens is the module's own.
 *
 * A course opens on its ADMIN page, never the learner entry /lms/courses/<id>
 * ("This training can't be opened directly" for an admin; browser QA,
 * 2026-10-06). Which admin page depends on the role, which a plain function
 * cannot know: the card passes `courseHref` from useCourseAdminHref (the
 * editor, or the live view for a role the editor refuses). Without it the
 * live view, the page every role that reads courses may open.
 */
export function nextStepHref(
  link: NonNullable<VcroNextStep['link']>,
  recipientId: string,
  courseHref: (courseId: string) => string = courseLiveHref,
): string {
  const id = link.targetId ? encodeURIComponent(link.targetId) : null
  switch (link.kind) {
    case 'assignTraining':
      return link.targetId ? courseHref(link.targetId) : '/lms/assignments'
    case 'sendReminder':
      return `/recipients/${encodeURIComponent(recipientId)}?tab=training`
    case 'openPhishTrainer':
      return id ? `/phishtrainer/users/${id}` : '/phishtrainer/users'
    case 'openCoaching':
      return id ? `/jit-training/campaigns/${id}` : '/jit-training/coaching-tips'
  }
}

/** A rate the API sends as a percent, 0..100 with one decimal: "41.2%", "81%". */
export function formatRate(percent: number, locale: string): string {
  return new Intl.NumberFormat(intlLocaleFor(locale), { style: 'percent', maximumFractionDigits: 1 }).format(percent / 100)
}

/**
 * One API count as a share of another, a whole percentage ("13%"); a share
 * that rounds to 0 but is not nothing reads "<1%". Null when there is no
 * whole to be a share of.
 */
export function formatShare(part: number, whole: number, locale: string): string | null {
  if (whole <= 0) return null
  const share = part / whole
  const f = new Intl.NumberFormat(intlLocaleFor(locale), { style: 'percent', maximumFractionDigits: 0 })
  if (part > 0 && share < 0.005) return `<${f.format(0.01)}`
  return f.format(share)
}
